import React, { useEffect, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { Search, X } from 'react-bootstrap-icons';
import { useSearchParams } from 'react-router-dom';

import { SortableTableHeader, useSortableTable } from '../../../../components/sortable-table-header';
import { SupportedCurrency } from '../../../../domain/currency';
import { CreatorStatsEntity, StatsEntity } from '../../../../domain/stats';
import { countAllOrders, formatOrderCount } from './ordersCount';

type CreatorsStatsForMonthProps = {
    stats: Pick<StatsEntity, 'creatorsStats' | 'creatorsStatsForAppliedContractor'>;
    forCurrency: SupportedCurrency;
}

type SortMode = 'orderCount' | 'totalProfit' | 'minProfit' | 'maxProfit' | 'averageProfit' | 'averageFreightValue';


export const CreatorsStatsForMonth = ({
    stats,
    forCurrency,
}: CreatorsStatsForMonthProps) => {
    const [appliedContractorView, setAppliedContractorView] = useState(!!stats.creatorsStatsForAppliedContractor);

    useEffect(() => {
        setAppliedContractorView(!!stats.creatorsStatsForAppliedContractor);
    }, [stats.creatorsStats, stats.creatorsStatsForAppliedContractor]);
    
    const [searchParams, setSearchParams] = useSearchParams();
    
    const { creatorsStats, creatorsStatsForAppliedContractor } = stats;

    const { sortBy, sortDirection, handleSort, sortItems } = useSortableTable<SortMode>({
        initialSortBy: 'orderCount',
        initialDirection: 'desc',
        comparator: (a: CreatorStatsEntity, b: CreatorStatsEntity, sortKey, direction) => {
            const multiplier = direction === 'desc' ? 1 : -1;
            switch (sortKey) {
                case 'orderCount':
                    return multiplier * (countAllOrders(b.orderCount) - countAllOrders(a.orderCount));
                case 'minProfit':
                    return multiplier * (b.minProfit - a.minProfit);
                case 'maxProfit':
                    return multiplier * (b.maxProfit - a.maxProfit);
                case 'averageProfit':
                    return multiplier * (b.averageProfit - a.averageProfit);
                case 'totalProfit':
                    return multiplier * (b.totalProfit - a.totalProfit);
                case 'averageFreightValue':
                    return multiplier * (b.averageFreightValue - a.averageFreightValue);
                default:
                    return 0;
            }
        },
    });

    const sortedCreatorsStats = sortItems(appliedContractorView ? creatorsStatsForAppliedContractor?.stats || [] : creatorsStats);

    if (sortedCreatorsStats.length === 0) {
        return (
            <>
                <h4>Spedytorzy</h4>
                <p className="text-muted">Brak spedytorów w wybranym okresie</p>
            </>
        );
    }

    const formatWithCurrency = (value: number) => value.toLocaleString('pl-PL', {
        style: 'currency',
        currency: forCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const handleCreatorSearch = (creatorId: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('creatorId', creatorId);
        setSearchParams(searchParams);
    };

    const handleCreatorFilterRemove = () => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('creatorId');
        setSearchParams(searchParams);
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Spedytorzy ({sortedCreatorsStats.length})</h4>
                {stats.creatorsStatsForAppliedContractor && (
                    <Form.Select
                        className="w-auto"
                        value={appliedContractorView ? 'applied' : 'all'}
                        onChange={(e) => setAppliedContractorView(e.target.value === 'applied')}
                    >
                        <option value="all">Wszyscy</option>
                        <option value="applied">Dla wybranego podwykonawcy</option>
                    </Form.Select>
                )}
            </div>
            <Table striped bordered hover responsive>
                <thead>
                <tr>
                    <th rowSpan={2}></th>
                    <th rowSpan={2} className="text-center text-nowrap">Nazwa</th>
                    <SortableTableHeader
                        label="Liczba zleceń"
                        sortKey="orderCount"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        rowSpan={2}
                    />
                    <th
                        colSpan={4}
                        className="text-center text-nowrap"
                    >
                        Marża
                    </th>
                    <SortableTableHeader
                        label="Śr. wartość frachtu"
                        sortKey="averageFreightValue"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        rowSpan={2}
                    />
                </tr>
                <tr>
                    <SortableTableHeader
                        label="Min"
                        sortKey="minProfit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Maks"
                        sortKey="maxProfit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Średnia"
                        sortKey="averageProfit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Suma"
                        sortKey="totalProfit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                </tr>
                </thead>
                <tbody>
                {sortedCreatorsStats.map((creatorStats) => (
                    <tr key={creatorStats.id}>
                        <td className="width-0 p-0 text-center align-middle">
                            <Button
                                className="m-0 p-0 px-1 d-flex align-items-center justify-content-center mx-auto"
                                variant="link"
                                onClick={() => handleCreatorSearch(creatorStats.id)}
                            >
                                {searchParams.get('creatorId') === creatorStats.id ? (
                                    <X onClick={(e) => {
                                        e.stopPropagation();
                                        handleCreatorFilterRemove();
                                    }} />
                                ) : (
                                    <Search />
                                )}
                            </Button>
                        </td>
                        <td className="width-250">
                            <span className={`text-primary ${searchParams.get('creatorId') === creatorStats.id ? 'fw-bold' : ''}`} style={{ cursor: 'default' }}>{creatorStats.name}</span>
                        </td>
                        <td className="text-center width-0 align-middle">{formatOrderCount(creatorStats.orderCount)}</td>
                        <td className={`text-center width-0 align-middle ${creatorStats.minProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatWithCurrency(creatorStats.minProfit)}
                        </td>
                        <td className={`text-center width-0 align-middle ${creatorStats.maxProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatWithCurrency(creatorStats.maxProfit)}
                        </td>
                        <td className={`text-center width-0 align-middle ${creatorStats.averageProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatWithCurrency(creatorStats.averageProfit)}
                        </td>
                        <td className={`text-center width-0 align-middle ${creatorStats.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatWithCurrency(creatorStats.totalProfit)}
                        </td>
                        <td className="text-center width-0 align-middle">
                            {formatWithCurrency(creatorStats.averageFreightValue)}
                        </td>
                    </tr>
                ))}
                </tbody>
            </Table>
        </>
    );
};
